import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { StatusScreen } from '@/components/StatusScreen';
import { INTEGRATIONS_FUNCTION } from '@/lib/chatFunction';

export default function GitHubCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) return;

    // If not authenticated, redirect to login preserving the current URL
    if (!user) {
      const returnUrl = location.pathname + location.search;
      navigate('/auth', {
        replace: true,
        state: { returnUrl }
      });
      return;
    }

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(`Erreur GitHub: ${searchParams.get('error_description') || errorParam}`);
        return;
      }

      if (!code || !state) {
        setError('Paramètres manquants');
        return;
      }

      try {
        const redirectUri = `${window.location.origin}/auth/github/callback`;

        const { data, error } = await supabase.functions.invoke(INTEGRATIONS_FUNCTION, {
          body: {
            action: 'githubCallback',
            code,
            state,
            redirectUri,
          },
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        // Success - redirect back to home with success message
        navigate('/?github=connected', { replace: true });
      } catch (err) {
        console.error('GitHub callback error:', err);
        setError(err instanceof Error ? err.message : 'Erreur lors de la connexion');
      }
    };

    handleCallback();
  }, [searchParams, navigate, user, isLoading, location]);

  if (error) {
    return (
      <StatusScreen
        status="error"
        title="Erreur de connexion"
        meta={error}
        action={{ label: "Retour à l'accueil", onClick: () => navigate('/') }}
      />
    );
  }

  return <StatusScreen status="loading" title="Connexion à GitHub…" />;
}
